import { Div } from "../../Div";
import { Input } from "../Input";
import type { IFormRenderer } from "../Renderer/IFormRenderer";
import { DefaultFormRenderer } from "../Renderer/DefaultFormRenderer";
import { RendererRegistry } from "../Renderer/RendererRegistry";
import { Tag } from "../../Tag";

export class FormItem extends Div {

    private static readonly REGEXP_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    public getName() {
        return this.getAttribute("id");
    }

    public reset() {
        this.setState("idle");
        if (this.getITag()) { this.getITag().clear(); }
    }

    public getValue(): string | boolean {
        return this.getITag().getText();
    }

    public getType() {
        return "FormType";
    }

    public getTitle(): string {
        return this.getAttr("t");
    }

    public getAttr(name: string) {
        const tag = this.findByAttribute("info");
        if (tag) { return tag.getAttribute(name); }
        // Contrôle substitué IN PLACE (pas de wrapper .formi-bare — cas du re-render
        // SPA, HA-44) : la racine EST l'input, les attributs moteur (n, t, choices…)
        // vivent sur elle et findByAttribute ne cherche que les DESCENDANTS.
        const el = this.getElement();
        return el ? el.getAttribute(name) : null;
    }

    public getITag(): Input {
        return this.get("itag");
    }

    public setState(state: "idle" | "loading" | "error" | "success", msg?: string): void {
        this.getRenderer().renderState(this.getElement(), state, msg);
    }

    public validate(): boolean {
        const rules = this.getValidationRules();
        if (!rules) { return true; }

        const value = (this.getValue() as string) || "";

        if (rules.required && !value.trim()) {
            this.setState("error", rules.requiredMsg || "Champ requis");
            return false;
        }

        if (rules.type === "email" && value && !FormItem.REGEXP_EMAIL.test(value)) {
            this.setState("error", rules.typeMsg || "Email invalide");
            return false;
        }

        if (rules.min && value.length < rules.min) {
            this.setState("error", rules.minMsg || `Minimum ${rules.min} caractères`);
            return false;
        }

        if (rules.max && value.length > rules.max) {
            this.setState("error", rules.maxMsg || `Maximum ${rules.max} caractères`);
            return false;
        }

        if (rules.pattern && value && !new RegExp(rules.pattern).test(value)) {
            this.setState("error", rules.patternMsg || "Format invalide");
            return false;
        }

        this.setState("idle");
        return true;
    }

    protected getRenderer(): IFormRenderer {
        // Précédence : renderer propre à l'item (clé data en phase build —
        // stampée par le converter mockup —, attribut DOM en phase contrôle où
        // l'item est reconstruit depuis l'élément existant) > renderer du form
        // (_renderer, propagé aux enfants directs seulement) > Default.
        const own = this.get("renderer")
            || (this.getElement() ? this.getAttribute("renderer") : null);
        if (own) { return RendererRegistry.create(own); }
        return this.get("_renderer") || new DefaultFormRenderer();
    }

    protected getValidationRules(): any {
        const direct = this.get("validate");
        if (direct) {
            if (typeof direct === "string") {
                try { return JSON.parse(direct); } catch (e) { /* ignore */ }
            } else {
                return direct;
            }
        }
        const encoded = this.getAttr("validate");
        if (typeof encoded === "string" && encoded.length > 0) {
            try { return JSON.parse(encoded); } catch (e) { /* ignore */ }
        }
        return null;
    }

    protected createItemTag(formItem: any): Tag {
        const v = this.get("validate") as any;
        return this.getRenderer().renderWrapper({
            id:         this.getId() || "fi" + this.get("name"),
            name:       this.get("name"),
            label:      this.trans("label"),
            max:        this.get("max"),
            choices:    this.translate(this.get("choices")),
            hint:       this.trans("hint"),
            icon:       this.get("icon"),
            required:   !!(v && v.required),
            extraClass: this.get("class") || this.get("className"),
            validate:   v,
        }, formItem);
    }
}
